import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
    CircularProgress,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import { GET_JOB_APPLICATIONS_STATS } from '@gql/jobs';
import { jobApplicationStatusLabels, JobApplicationStatus } from './statuses';

export const StatsModal = ({
    isStatsModalOpen,
    handleStatsModalClose,
}) => {
    const [fetchStats, { loading, data, called }] = useLazyQuery(GET_JOB_APPLICATIONS_STATS, {
        fetchPolicy: 'cache-and-network',
    });

    useEffect(() => {
        if (isStatsModalOpen && !called) {
            fetchStats();
        }
    }, [isStatsModalOpen, called, fetchStats]);

    // Bind values from GraphQL
    const stats = (data?.getJobApplicationsStats || {}) as any;
    const {
        new: newCount = 0,
        interview = 0,
        trial = 0,
        offer = 0,
        probationPeriod = 0,
        hired = 0,
        rejected = 0,
    } = stats;
    const total = newCount + interview + trial + offer + probationPeriod + hired + rejected;

    // Map status keys to their corresponding values
    const statusValues = {
        [JobApplicationStatus.New]: newCount,
        [JobApplicationStatus.Interview]: interview,
        [JobApplicationStatus.Trial]: trial,
        [JobApplicationStatus.Offer]: offer,
        [JobApplicationStatus.ProbationPeriod]: probationPeriod,
        [JobApplicationStatus.Hired]: hired,
        [JobApplicationStatus.Rejected]: rejected,
    };

    const renderStatusBox = (status: JobApplicationStatus, value: number, isTotal = false) => (
        <Box
            key={status}
            sx={{
                background: isTotal ? '#5B48D9' : '#2A2D30',
                border: `1px solid ${isTotal ? '#5B48D9' : '#3A3D40'}`,
                borderRadius: '8px',
                padding: '16px 20px',
                minHeight: '70px',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                position: 'relative',
                zIndex: 2
            }}
        >
            <Typography sx={{
                fontSize: '20px',
                fontWeight: 400,
                fontStyle: 'normal',
                lineHeight: '100%',
                letterSpacing: '0%',
                color: '#FFFFFF'
            }}>
                {isTotal ? 'Total' : jobApplicationStatusLabels[status]}
            </Typography>
            <Typography sx={{
                fontSize: '28px',
                fontWeight: 700,
                fontStyle: 'normal',
                lineHeight: '100%',
                letterSpacing: '0%',
                color: '#FFFFFF'
            }}>
                {value}
            </Typography>
        </Box>
    );

    return (
        <Dialog
            open={isStatsModalOpen}
            onClose={handleStatsModalClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    background: theme => `#242629`,
                    border: theme => `1px solid ${theme.palette.neutralDarkGray.main}`,
                    minHeight: '400px',
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-around',
                alignItems: 'center',
            }}>
                <Typography variant="h6" sx={{
                    fontWeight: 700,
                    fontSize: '28px',
                    marginRight: '35px',
                    color: theme => theme.palette.neutralWhite.main
                }}>
                    Résumé statistique
                </Typography>
                <IconButton
                    onClick={handleStatsModalClose}
                    sx={{
                        color: theme => theme.palette.neutralWhite.main,
                        background: theme => theme.palette.neutralBlack.main,
                        border: theme => `1px solid ${theme.palette.neutralDarkGray.main}`,
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        '&:hover': {
                            background: theme => theme.palette.neutralDarkGray.main,
                        }
                    }}
                >
                    <Close sx={{ fontSize: '16px' }} />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', justifyContent: 'center' }}>
                {loading ? (
                    <Box sx={{
                        width: 462,
                        height: 388,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : (
                    // Stats Grid - 4x2 layout
                    <Box sx={{
                        width: 462,
                        height: 388,
                        background: '#222426',
                        borderRadius: '16px',
                        border: theme => `1px solid rgba(255, 255, 255, 0.03)`,
                        padding: '24px 28px',
                        opacity: 1,
                        marginBottom: '16px'
                    }}>
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '20px',
                            width: '100%',
                            height: '100%'
                        }}>
                            {/* Render status boxes */}
                            {Object.keys(jobApplicationStatusLabels).map((status) =>
                                renderStatusBox(status as JobApplicationStatus, statusValues[status as JobApplicationStatus])
                            )}

                            {/* Total box */}
                            {renderStatusBox('Total' as any, total, true)}
                        </Box>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};
